/**
 * @file MarkerClusterer setup with a branded, numbered cluster badge.
 *
 * Clusters our AdvancedMarkerElement markers; the cluster bubble is itself an
 * AdvancedMarkerElement showing the count. Per-category filtering and radius
 * refetch are handled by the engine rebuilding the clusterer's marker set
 * (clearMarkers + addMarkers) — never by CSS-hiding, so counts stay correct.
 */
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
  type Renderer,
} from "@googlemaps/markerclusterer";

/** Numbered cluster badge; size steps up with the count. Styled in SCSS. */
function buildClusterContent(count: number): HTMLElement {
  const el = document.createElement("div");
  el.className = "locatorCluster";
  const size = count < 10 ? 34 : count < 50 ? 42 : 52;
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.textContent = String(count);
  return el;
}

const renderer: Renderer = {
  render({ count, position }) {
    return new google.maps.marker.AdvancedMarkerElement({
      position,
      content: buildClusterContent(count),
      // Keep clusters above individual markers.
      zIndex: 1000 + count,
    });
  },
};

export function createClusterer(map: google.maps.Map): MarkerClusterer {
  return new MarkerClusterer({
    map,
    markers: [],
    algorithm: new SuperClusterAlgorithm({ radius: 100, maxZoom: 15 }),
    renderer,
    // Center the clicked cluster and zoom in a couple of levels so it expands
    // toward its markers. Use moveCamera (atomic center+zoom) — panTo animates
    // and a following setZoom interrupts it, leaving the map on the old center.
    onClusterClick: (_event, cluster, clusterMap) => {
      const z = clusterMap.getZoom() ?? 8;
      clusterMap.moveCamera({
        center: cluster.position,
        zoom: Math.min(z + 2, 16),
      });
    },
  });
}
